import { MotionDiv } from '@/components/element/MotionDiv';
import { useQuotationTaskInvalidate } from '@/hooks/api/1480_quotation_task';
import { useUpdateQuotationInComment } from '@/hooks/api/1481_update_quotation_in_comment';
import { useUpdateBillingScheduleChecked } from '@/hooks/api/1482_update_billing_schedule_checked';
import { useQuotationAmountFromMf } from '@/hooks/api/1483_quotation_amount_from_mf';
import { TeamMemberLogic } from '@/hooks/api/models/logics';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuotationBoardAmount } from '@/hooks/api/2520_quotation_board_amount';

export const ToDoList = () => {
  return (
    <MotionDiv className="card mb-4">
      <div className="card-header">
        <h6 className="mb-1">TODOリスト</h6>
        <p className="text-xs mb-0">TODOを処理して、タスクを次のフェーズへ移動させてください。</p>
      </div>
      <div className="card-body pt-0">
        <ul className="list-group list-group-flush" data-toggle="checklist">
          <QuotationInCommentElement />
          <BillingScheduleCheckElement />
        </ul>
      </div>
    </MotionDiv>
  );
};

const QuotationInCommentElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, data, isPending, error } = useUpdateQuotationInComment();
  const { invalidate } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    id: task!.id,
  });
  const [disabled, setDisabled] = useState(true);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onQuotationInCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id!),
      id: task!.id,
      quotation_in_comment: e.target.checked,
    });
  };

  useEffect(() => {
    if (task) {
      setDisabled(task!.quotation_task_phase.name != 'preparing');
    }
  }, [task]);

  useEffect(() => {
    if (data) {
      invalidate();
    }
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.has_document || task!.quotation_in_comment ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            見積書の添付
            {task!.has_document || task!.quotation_in_comment ? (
              <></>
            ) : (
              <i className="fas fa-exclamation-circle ms-1 text-danger"></i>
            )}
          </h6>
          <div className="text-weak text-xs mt-1">承認依頼対象の見積書を添付してください。</div>
        </div>
        {!task!.has_document && (
          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="quotationInCommentCheck"
              checked={task!.quotation_in_comment}
              onChange={onQuotationInCommentChange}
              disabled={disabled}
            />
            <label className="custom-control-label" htmlFor="quotationInCommentCheck">
              添付せずにコメント欄に見積内容を記載
            </label>
          </div>
        )}
      </div>
      <hr className="horizontal dark mt-4 mb-0" />
    </li>
  );
};

const BillingScheduleCheckElement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, data, isPending, error } = useUpdateBillingScheduleChecked();
  const {
    data: quotationAmountFromMf,
    isLoading: isQuotationAmountMfPending,
    error: isQuotationAmountMfError,
    refetch: refetchQuotationAmountFromMf,
  } = useQuotationAmountFromMf({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    id: task!.id,
    enabled: false,
  });
  const {
    data: quotationBoardAmount,
    isLoading: isQuotationBoardAmountPending,
    error: isQuotationBoardAmountError,
    refetch: refetchQuotationBoardAmount,
  } = useQuotationBoardAmount({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    enabled: false,
  });
  const { invalidate } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    id: task!.id,
  });
  const [disabled, setDisabled] = useState(true);

  useEffect(
    () => setShowLoading(isPending || isQuotationAmountMfPending || isQuotationBoardAmountPending),
    [isPending, isQuotationAmountMfPending, isQuotationBoardAmountPending]
  );
  useEffect(
    () => toastApiError(error || isQuotationAmountMfError || isQuotationBoardAmountError),
    [error, isQuotationAmountMfError, isQuotationBoardAmountError]
  );

  const onBillingScheduleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id!),
      id: task!.id,
      billing_schedule_checked: e.target.checked,
    });
  };

  const onQuotationAmountFromMfClick = () => {
    refetchQuotationAmountFromMf();
  };

  const onQuotationBoardAmountClick = () => {
    refetchQuotationBoardAmount();
  };

  useEffect(() => {
    if (task) {
      setDisabled(task!.quotation_task_phase.name != 'preparing');
    }
  }, [task]);

  useEffect(() => {
    if (data) {
      invalidate();
    }
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 pe-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.billing_schedule_checked ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            プロジェクト計画金額や期間の最新化
            {task!.billing_schedule_checked ? <></> : <i className="fas fa-exclamation-circle ms-1 text-danger"></i>}
          </h6>
          <div className="text-weak text-xs mt-1">
            {!disabled && (
              <>
                プロジェクトの計画金額や期間が変更になっている場合、
                <Link
                  to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${sales_order_id}/edit`}
                  state={{ prevPath: location.pathname }}
                  style={{ textDecoration: 'underline' }}
                >
                  こちら
                </Link>
                から変更してください。
              </>
            )}
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12 col-md-5">
            <div className="form-group">
              <label className="mx-0">プロジェクト計画金額（税抜）</label>
              <div className="form-control-plaintext">
                ¥{task!.sales_order.accounting_total_amount.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-7">
            {_w_!.money_forward_setting != null && (
              <div className="form-group">
                <label className="mx-0">見積合計金額</label>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="form-control-plaintext flex-1">
                    ¥{quotationAmountFromMf ? quotationAmountFromMf.amount.toLocaleString() : ' -'}
                  </div>
                  <button
                    className="btn btn-icon bg-gradient-dark ms-1 btn-sm px-3"
                    type="button"
                    disabled={disabled}
                    onClick={onQuotationAmountFromMfClick}
                  >
                    見積書から取得
                  </button>
                </div>
              </div>
            )}
            {task!.sales_order.sales_order_board_project != null && (
              <div className="form-group">
                <label className="mx-0">見積合計金額</label>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="form-control-plaintext flex-1">
                    ¥{quotationBoardAmount ? quotationBoardAmount.amount.toLocaleString() : ' -'}
                  </div>
                  <button
                    className="btn btn-icon bg-gradient-dark ms-1 btn-sm px-3"
                    type="button"
                    disabled={disabled}
                    onClick={onQuotationBoardAmountClick}
                  >
                    boardから取得
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="billingScheduleCheck"
            checked={task!.billing_schedule_checked}
            onChange={onBillingScheduleChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor="billingScheduleCheck">
            プロジェクトの期間や金額が正しいことを確認
          </label>
        </div>
      </div>
    </li>
  );
};
